/**
 * pages/create-couple.tsx
 *
 * Create Couple screen — step 2 of onboarding.
 * Generates a new couple and displays the 6-char invite code.
 * Partner can join via /join with this code.
 */
import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useCreateCouple } from '../hooks/use-couple'

const StepDots = ({ current, total }: { current: number; total: number }) => (
  <div className="flex items-center justify-center gap-2 mb-8">
    {Array.from({ length: total }, (_, i) => (
      <div
        key={i}
        className={`rounded-full transition-all duration-300 ${
          i + 1 === current
            ? 'w-6 h-2 bg-[#D4537E]'
            : 'w-2 h-2 bg-[#F4C0D1]'
        }`}
      />
    ))}
  </div>
)

export default function CreateCouplePage() {
  const navigate = useNavigate()
  const { mutate: createCouple, isPending, error, data } = useCreateCouple()

  // Auto-create couple on mount
  useEffect(() => {
    createCouple({})
  }, [createCouple])

  const code = data?.data?.couple?.invite_code ?? ''

  const handleCopy = () => {
    if (code) {
      navigator.clipboard.writeText(code).catch(() => {})
    }
  }

  return (
    <div className="min-h-screen bg-[#FAF8F4] flex flex-col max-w-[430px] mx-auto">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-[#FAF8F4] px-5 pt-[env(safe-area-inset-top)] pb-2">
        <div className="flex items-center gap-2 h-12">
          <button
            type="button"
            onClick={() => navigate('/register')}
            className="flex items-center gap-1 text-[#D4537E] font-['DM_Sans'] text-[15px] active:opacity-60 transition-opacity duration-100 touch-manipulation -ml-1"
          >
            ← Back
          </button>
        </div>
      </header>

      {/* Content */}
      <main className="flex-1 px-5 pb-[88px] overflow-y-auto">
        <div className="animate-fade-in-up">
          <StepDots current={2} total={3} />

          {/* Title */}
          <div className="mb-8 [animation-delay:60ms] animate-fade-in-up">
            <h1 className="font-['Playfair_Display'] text-[28px] font-semibold text-[#2C1810] leading-tight mb-2">
              Your invite code
            </h1>
            <p className="font-['DM_Sans'] text-[14px] text-[#7A5A62]">
              Share this with your partner so they can join your private space.
            </p>
          </div>

          {/* Invite Code Card */}
          <div className="mb-6 [animation-delay:90ms] animate-fade-in-up">
            {isPending ? (
              <div className="bg-white rounded-[20px] shadow-[0_8px_32px_rgba(212,83,126,0.12)] p-6 text-center">
                <div className="flex flex-col items-center justify-center gap-3 py-4">
                  <div className="text-[32px] animate-float">💕</div>
                  <p className="font-['DM_Sans'] text-[13px] text-[#B89AA2]">Creating your space…</p>
                </div>
              </div>
            ) : error ? (
              <div className="bg-white rounded-[20px] border border-[#F5C6CB] p-6 text-center">
                <p className="font-['DM_Sans'] text-[14px] text-[#C0392B] mb-4">
                  {error.message}
                </p>
                <button
                  type="button"
                  onClick={() => createCouple({})}
                  className="
                    w-full h-[52px] px-6
                    bg-[#D4537E] text-white
                    font-['DM_Sans'] text-[15px] font-medium
                    rounded-[12px]
                    shadow-[0_4px_16px_rgba(212,83,126,0.28)]
                    transition-all duration-150 ease-out
                    active:bg-[#993556] active:scale-[0.98] active:shadow-none
                    select-none touch-manipulation
                  "
                >
                  Try again
                </button>
              </div>
            ) : code ? (
              <div className="bg-white rounded-[20px] shadow-[0_8px_32px_rgba(212,83,126,0.12)] p-6 text-center">
                <p className="font-['DM_Sans'] text-[13px] text-[#B89AA2] mb-2">
                  Your invite code
                </p>
                <span className="font-['DM_Mono'] text-[32px] font-medium text-[#D4537E] tracking-[0.14em] block mb-2">
                  {code.slice(0, 3)} · {code.slice(3)}
                </span>
                <p className="font-['DM_Sans'] text-[11px] text-[#B89AA2]">
                  Expires never
                </p>
              </div>
            ) : null}
          </div>

          {/* Copy code button */}
          {code && (
            <div className="mb-4 [animation-delay:120ms] animate-fade-in-up">
              <button
                type="button"
                onClick={handleCopy}
                className="
                  w-full h-[52px] px-6
                  bg-[#FBEAF0] text-[#D4537E]
                  font-['DM_Sans'] text-[15px] font-medium
                  rounded-[12px] border border-[#F4C0D1]
                  transition-all duration-150 ease-out
                  active:bg-[#F4C0D1] active:scale-[0.98]
                  select-none touch-manipulation
                "
              >
                Copy code
              </button>
            </div>
          )}

          {/* Divider */}
          {code && (
            <div className="flex items-center gap-3 mb-4 [animation-delay:150ms] animate-fade-in-up">
              <div className="flex-1 h-px bg-[#F4C0D1]" />
              <span className="font-['DM_Sans'] text-[12px] text-[#B89AA2]">or</span>
              <div className="flex-1 h-px bg-[#F4C0D1]" />
            </div>
          )}

          {/* Already have a code? */}
          {code && (
            <div className="mb-6 [animation-delay:180ms] animate-fade-in-up">
              <button
                type="button"
                onClick={() => navigate('/join')}
                className="
                  w-full h-[52px] px-6
                  bg-transparent text-[#7A5A62]
                  font-['DM_Sans'] text-[15px] font-medium
                  rounded-[12px] border border-[#F4C0D1]
                  transition-all duration-150 ease-out
                  active:bg-[#FBEAF0]
                  select-none touch-manipulation
                "
              >
                Enter a partner's code instead
              </button>
            </div>
          )}

          {/* How it works */}
          {code && (
            <div className="bg-[#FBEAF0] rounded-[16px] border border-[#F4C0D1] p-4 mb-6 [animation-delay:210ms] animate-fade-in-up">
              <p className="font-['DM_Sans'] text-[13px] font-medium text-[#7A5A62] mb-2">How it works</p>
              <ol className="flex flex-col gap-1.5">
                {[
                  'Share this code with your partner',
                  'They open Just Us and tap "Join with code"',
                  "You'll both be connected in your private space",
                ].map((step, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className="font-['DM_Mono'] text-[12px] text-[#D4537E] mt-0.5 flex-shrink-0">
                      {i + 1}.
                    </span>
                    <span className="font-['DM_Sans'] text-[13px] text-[#7A5A62]">{step}</span>
                  </li>
                ))}
              </ol>
            </div>
          )}

          {/* Continue button */}
          {code && (
            <div className="[animation-delay:240ms] animate-fade-in-up">
              <button
                type="button"
                onClick={() => navigate('/anniversary')}
                className="
                  w-full h-[52px] px-6
                  bg-[#D4537E] text-white
                  font-['DM_Sans'] text-[15px] font-medium
                  rounded-[12px]
                  shadow-[0_4px_16px_rgba(212,83,126,0.28)]
                  transition-all duration-150 ease-out
                  active:bg-[#993556] active:scale-[0.98] active:shadow-none
                  select-none touch-manipulation
                "
              >
                Continue →
              </button>
            </div>
          )}
        </div>
      </main>
    </div>
  )
}
